import * as zod from 'zod';

export const violationLimitsSchema = zod.object({
  /** Maximum allowed for L/D to start before L/D window start */
  early: zod.number().nullable().optional(),
  /** Maximum allowed for L/D to start after L/D window end */
  late: zod.number().nullable().optional(),
});
export type ViolationLimits = zod.infer<typeof violationLimitsSchema>;
export const orderViolationLimitsSchema = zod.object({
  load: violationLimitsSchema,
  discharge: violationLimitsSchema,
});
export type OrderViolationLimits = zod.infer<typeof orderViolationLimitsSchema>;

export const orderSelectionParamsSchema = zod.object({
  orderSelection: zod.enum([
    'all',
    'only_visible',
    'only_selected',
    'exclude_selected',
    'shortlist',
  ]),
  orderTypes: zod.object({
    coaPreliminaries: zod.boolean(),
    fixedCoas: zod.boolean(),
    fixedSpots: zod.boolean(),
    inquiries: zod.boolean(),
  }),
});

export type OrderSelectionParams = zod.infer<typeof orderSelectionParamsSchema>;

export const weightParamsSchema = zod.object({
  am: zod.number().min(0),
  dm: zod.number().max(0),
  atm: zod.number().min(0),
  dtm: zod.number().max(0),
  maxTime: zod.number().min(1).max(300),
  capacity: zod.number().min(0).nullable(),
  group: zod.number().min(0).nullable(),
  limit: zod.number().min(0).nullable(),
  order: zod.number().min(0).nullable(),
  time: zod.number().min(0).nullable(),
  volume: zod.number().min(0).nullable(),
  allowedExcess: zod.number().min(0).nullable(),
  allowedCost: zod.number().min(0).nullable(),
});

export type WeightParams = zod.infer<typeof weightParamsSchema>;

export const DEFAULT_WEIGHT_PARAMS = {
  am: 0,
  dm: 0,
  atm: 1,
  dtm: -1,
  maxTime: 30,
  capacity: 1000,
  group: 1e9,
  limit: 1e8,
  order: 1e9,
  time: 1,
  volume: 1000,
  allowedExcess: 1e8,
  allowedCost: 1e4,
} satisfies WeightParams;

export const externalImprovementParamsSchema = zod.object({
  ...orderSelectionParamsSchema.shape,
  vesselIds: zod.array(zod.number()),
  firstAllowedTime: zod.number().default(() => Date.now()),
  weights: weightParamsSchema,
  onlyAddToEnd: zod.boolean().default(true),
});

export const seaberImprovementParamsSchema = zod.object({
  cargoExclusions: zod.enum(['warn', 'exclude']),
  draftRestrictions: zod.enum(['warn', 'exclude']),
  /** Max allowed early/late for any order event */
  violationLimitsD: orderViolationLimitsSchema,
});

export const scenarioImprovementsParamsSchema = zod.object({
  method: zod.enum([
    'external_best_add',
    'external_best_group_add',
    'external_solve',
    'seaber',
  ]),
  externalSolver: externalImprovementParamsSchema,
  seaber: seaberImprovementParamsSchema,
});
export type ScenarioImprovementsParams = zod.infer<
  typeof scenarioImprovementsParamsSchema
>;

export const getScenarioImprovementsParamDefaults: () => ScenarioImprovementsParams =
  () => ({
    method: 'seaber',
    externalSolver: {
      orderSelection: 'all',
      orderTypes: {
        coaPreliminaries: true,
        fixedCoas: true,
        fixedSpots: true,
        inquiries: true,
      },
      vesselIds: [],
      firstAllowedTime: Date.now(),
      weights: DEFAULT_WEIGHT_PARAMS,
      onlyAddToEnd: true,
    },
    seaber: {
      cargoExclusions: 'warn',
      draftRestrictions: 'warn',
      violationLimitsD: {
        load: {
          early: 7,
          late: 7,
        },
        discharge: {
          early: 7,
          late: 7,
        },
      },
    },
  });
